import CustomTransferList from 'Components/CustomTransferList'
import { WithLoader } from 'Components/WithLoader'
import {
  useGetCatalogsQuery,
  useUpdateSingleCatalogMutation
} from 'Services/ProductManagement/Features/Catalog/catalogApi'
import {
  fetchSingleProduct,
  type Product
} from 'Services/ProductManagement/Features/Product/productSlice'
import { TariffContext } from 'Services/ProductManagement/Features/Tariff/Context/tariffContext'
import { useAppDispatch } from 'Store'
import { ROWS_PER_PAGE_DEFAULT } from 'Util/constants'
import SnackbarUtils from 'Util/Snackbar'
import { Box, ListItemText } from '@mui/material'
import { useContext } from 'react'
import { t } from 'ttag'

interface ProductCatalogsProps {
  product: Product
  handleClose: () => void
}

export function ProductCatalogs({ product, handleClose }: Readonly<ProductCatalogsProps>) {
  const dispatch = useAppDispatch()
  const { selectedTariffId } = useContext(TariffContext)

  const [updateSingleCatalog, { isLoading: isUpdateLoading }] = useUpdateSingleCatalogMutation()
  const { data: { resources: catalogs } = { resources: [] }, isFetching: isCatalogsFetching } =
    useGetCatalogsQuery({
      bypass_pagination: true,
      tariff_id: selectedTariffId
    })

  const options = catalogs.map(catalog => catalog.id)
  const catalogsWithoutSelectedProduct = options.filter(
    catalogId => !product.catalog_ids?.includes(catalogId)
  )

  const renderItemContent = (itemId: string) => {
    const catalog = catalogs.find(catalog => catalog.id === itemId)
    const labelId = `transfer-list-item-${itemId}-label`
    return <ListItemText id={labelId} primary={catalog?.name} />
  }

  const refreshProductData = () => {
    dispatch(fetchSingleProduct({ id: product.id }))
  }

  /**
   * @param rightItems - Array of catalog IDs that the product should be added to.
   * @returns void
   *
   * Loops through the rightItems array and updates the product IDs in the catalogs.
   * Also removes the product ID from catalogs that are removed from the rightItems.
   */
  const handleSave = async (rightItems: string[]) => {
    const initialCatalogIds = product.catalog_ids ?? []

    try {
      for (const catalogId of rightItems) {
        const catalog = catalogs.find(catalog => catalog.id === catalogId)

        if (catalog) {
          // Add product ID to catalog if it doesn't already exist.
          if (!catalog.product_ids.includes(product.id)) {
            const newProductIds = [...(catalog.product_ids || []), product.id]
            await updateSingleCatalog({
              id: catalog.id,
              product_ids: newProductIds
            }).unwrap()
          }
        }
      }

      /**
       * Remove product ID from catalogs not in rightItems.
       * Loop through initialCatalogIds to find catalogs that previously contained the product ID
       * but are not in the rightItems anymore.
       */
      for (const catalogId of initialCatalogIds) {
        if (!rightItems.includes(catalogId)) {
          const catalog = catalogs.find(catalog => catalog.id === catalogId)

          if (catalog) {
            const newProductIds = catalog.product_ids.filter(id => id !== product.id)
            await updateSingleCatalog({
              id: catalog.id,
              product_ids: newProductIds
            }).unwrap()
          }
        }
      }
      SnackbarUtils.success(t`Product's sales channels updated successfully.`)
    } catch (e) {
      console.error(e)
      SnackbarUtils.error(t`There was an error updating the product to sales channels.`)
    }
    refreshProductData()
  }

  const isLoading = isCatalogsFetching || isUpdateLoading

  return (
    <WithLoader rows={ROWS_PER_PAGE_DEFAULT} loading={isLoading} height={33}>
      <Box sx={{ width: '100%', mt: 2 }}>
        <CustomTransferList
          leftItemsHeaderText={t`Sales channels`}
          rightItemsHeaderText={t`Product's sales channels`}
          leftItems={catalogsWithoutSelectedProduct}
          rightItems={product.catalog_ids ?? []}
          renderItemContent={renderItemContent}
          handleSave={handleSave}
          handleCancel={handleClose}
          compareFn={(item: string) => item}
        />
      </Box>
    </WithLoader>
  )
}
